'use client'
import React, { useState, useEffect, useCallback, useMemo } from "react";
import Link from "next/link";
import useEmblaCarousel from "embla-carousel-react";
import { useScrollAnimation, useStaggeredAnimation } from "../../hooks/useScrollAnimation";
import { getVehiculosDestacados } from "@/lib/strapi-simple";

export function HomeProducts() {
  const [headerRef, headerVisible] = useScrollAnimation();
  const [carouselRef, carouselVisible] = useScrollAnimation();
  const [productsData, setProductsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Cargar datos de vehículos desde Strapi
  useEffect(() => {
    const loadVehiclesData = async () => {
      setIsLoading(true);
      try {
        const strapiVehiculos = await getVehiculosDestacados();
        
        if (strapiVehiculos && strapiVehiculos.length > 0) {
          // Usar datos de Strapi (ya transformados)
          const products = strapiVehiculos.map(vehiculo => ({
            id: vehiculo.id,
            name: `${vehiculo.marca} ${vehiculo.modelo}`,
            status: "Nuevo",
            price: `$${vehiculo.precio?.toLocaleString() || '0'}`,
            image: vehiculo.imagen,
            marca: vehiculo.marcaSlug,
            modelo: vehiculo.slug,
            categoria: vehiculo.categoria,
            year: vehiculo.año
          }));
          console.log('🚗 Productos desde Strapi:', products);
          setProductsData(products);
        } else {
          console.warn('No se encontraron vehículos en Strapi');
          setProductsData([]);
        }
      } catch (error) {
        console.error('Error loading vehicles data:', error);
        setProductsData([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadVehiclesData();
  }, []);
  
  const itemsPerPage = 5;
  const [emblaRef, emblaApi] = useEmblaCarousel({
    slidesToScroll: itemsPerPage,
    containScroll: "trimSnaps",
    loop: false,
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [snapCount, setSnapCount] = useState(0);

  // Calcular snapCount con useMemo
  const calculatedSnapCount = useMemo(() => {
    if (!emblaApi || productsData.length === 0) return 1;
    const slideNodes = emblaApi.slideNodes();
    return slideNodes.length - itemsPerPage + 1 > 0 ? Math.ceil(productsData.length / itemsPerPage) : 1;
  }, [emblaApi, itemsPerPage, productsData.length]);

  // Actualiza el número de snaps reales
  useEffect(() => {
    setSnapCount(calculatedSnapCount);
  }, [calculatedSnapCount]);

  // Actualiza la página actual
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      const snap = emblaApi.selectedScrollSnap();
      setSelectedIndex(snap);
    };
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  // Navegación optimizada con useCallback
  const scrollTo = useCallback(
    (snapIdx) => {
      if (!emblaApi) return;
      emblaApi.scrollTo(snapIdx);
    },
    [emblaApi]
  );

  const handlePrev = useCallback(() => {
    if (selectedIndex === 0) return;
    scrollTo(selectedIndex - 1);
  }, [selectedIndex, scrollTo]);

  const handleNext = useCallback(() => {
    if (selectedIndex === snapCount - 1) return;
    scrollTo(selectedIndex + 1);
  }, [selectedIndex, snapCount, scrollTo]);

  // Calcular propiedades de visualización con useMemo
  const displayProps = useMemo(() => ({
    showNavigation: productsData.length > 5,
    centerItems: productsData.length < 5
  }), [productsData.length]);

  return (
    <section className="max-w-[1400px] mx-auto p-4 pb-24">
      <div 
        ref={headerRef}
        className={`transition-all duration-1000 ease-out ${
          headerVisible 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
      >
        <p className="text-sm font-medium uppercase mb-1">Vehículos</p>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-3xl font-bold">Productos Destacados</h2>
            <p className="text-lg text-gray-600">
              Descubre nuestra selección de vehículos premium de las mejores marcas
            </p>
          </div>
          <Link 
            href="/vehiculos"
            prefetch={true}
            className="px-6 py-2 border border-black hover:bg-gray-100 transition-colors duration-300 text-center"
          >
            Ver todos
          </Link>
        </div>
      </div>

      <div
        ref={carouselRef}
        className={`embla overflow-hidden transition-all duration-1000 ease-out delay-300 ${
          carouselVisible 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}
        style={{ cursor: "grab" }}
      >
        <div ref={emblaRef} className="embla__viewport overflow-hidden">
        <div className={`embla__container flex gap-4${displayProps.centerItems ? ' justify-center' : ''}`}>
          {isLoading ? (
            // Skeleton loading
            [...Array(5)].map((_, idx) => (
              <div
                key={idx}
                className="embla__slide flex-shrink-0 bg-white rounded-md shadow-sm animate-pulse"
                style={{ width: "calc((100% - 64px) / 5)" }}
              >
                <div className="aspect-square bg-gray-200 rounded-t-md"></div>
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-8 bg-gray-200 rounded mt-4"></div>
                </div>
              </div>
            ))
          ) : (
            productsData.map(product => (
            <div
              key={product.id}
              className="embla__slide flex-shrink-0 bg-white rounded-md shadow-sm hover:shadow-md transition-shadow duration-300"
              style={{ width: "calc((100% - 64px) / 5)" }}
            >
              <div className="aspect-square overflow-hidden rounded-t-md">
                <img
                  src={product.image}
                  alt={product.name}
                  className="object-cover w-full h-full hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.src = "https://d22po4pjz3o32e.cloudfront.net/placeholder-image.svg";
                  }}
                />
              </div>
              <div className="p-4 flex flex-col justify-between h-[180px]">
                <div>
                  <h3 className="font-semibold text-lg line-clamp-1">{product.name}</h3>
                  <p className="text-sm text-gray-600">{product.categoria}</p>
                  <p className="text-xs text-gray-500">{product.year}</p>
                  <p className="font-bold mt-2 text-blue-600">{product.price}</p>
                </div>
                <Link 
                  href={`/vehiculos/${product.marca}/${product.modelo}`}
                  prefetch={true}
                  className="mt-4 w-full py-2 border border-black text-black text-center hover:bg-gray-100 transition-colors duration-300 block"
                >
                  Ver detalles
                </Link>
              </div>
            </div>
            ))
          )}
          </div>
        </div>
      </div>

      {/* Navegación */}
      {displayProps.showNavigation && (
        <div className="flex justify-between items-center mt-6">
          {/* Puntos */}
          <div className="flex items-center space-x-2">
            {[...Array(snapCount)].map((_, idx) => (
              <button
                key={idx}
                onClick={() => scrollTo(idx)}
                className={`w-2.5 h-2.5 rounded-full transition ${
                  selectedIndex === idx ? "bg-black" : "bg-gray-300"
                }`}
                aria-label={`Ir a página ${idx + 1}`}
              />
            ))}
          </div>

          {/* Flechas */}
          <div className="flex items-center space-x-4">
            <button
              onClick={handlePrev}
              disabled={selectedIndex === 0}
              className={`w-10 h-10 flex items-center justify-center border border-black rounded-full transition ${
                selectedIndex === 0
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-gray-100"
              }`}
              aria-label="Anterior"
            >
              ←
            </button>
            <button
              onClick={handleNext}
              disabled={selectedIndex === snapCount - 1}
              className={`w-10 h-10 flex items-center justify-center border border-black rounded-full transition ${
                selectedIndex === snapCount - 1
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-gray-100"
              }`}
              aria-label="Siguiente"
            >
              →
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
